#import "./fragments/groupFragment.graphql"
#import "./fragments/teamDetailFragment.graphql"
#import "./fragments/teamBudgetsFragment.graphql"
#import "./fragments/teamFragment.graphql"
#import "./fragments/objectiveFragment.graphql"
#import "./fragments/childGroupFragment.graphql"
#import "./fragments/personCardFragment.graphql"
#import "./fragments/personManagerFragment.graphql"
#import "./fragments/groupPermissions.graphql"
#import "./fragments/teamDetailFragment.graphql"
#import "src/components/TagsEditor/queries/fragments/tagFragment.graphql"

query getGroupQuery($groupId: String!) {
  getGroup(groupId: $groupId) {
    ...GroupFragment
    groupAssociations {
      tag {
        id
        tagTypeMetadata {
          name
        }
        type
        displayValue
      }
      group {
        ...GroupFragment
      }
      associationDirection {
        mode
        description
      }
    }
  }
}

query getTeamDetailsQuery($groupId: String!) {
  getGroup(groupId: $groupId) {
    ...TeamDetailFragment
    tags {
      id
      type
      displayValue
      isActive
      attributes
      tagAttributesMetaData {
        person {
          aggregateId
          preferredName
          firstName
          surname
          avatar
        }
      }
    }
    supplyOwners {
      aggregateId
      preferredName
      firstName
      surname
      avatar
      memberships(input: { membershipTypes: [OWNER] }) {
        currentCompositeId
        groupId
        ownerRole
      }
      supplyOwnerOf {
        id
        name
        type
        hierarchy
        hierarchyIds
        parentIds
      }
      memberOf {
        id
        name
        type
        hierarchy
        hierarchyIds
        parentIds
      }
    }
    supplyInferredOwnedBy {
      ...PersonCardFragment
    }
  }
}

query getTeamMembersQuery($groupId: String!) {
  team: getGroup(groupId: $groupId) {
    id
    members(limit: 500) {
      ...PersonCardFragment
      ...PersonManagerFragment
      isPlaceholder
      isVacancy
      span
    }
  }
}

# Lightweight, self-contained lookup for OwnerEditorInline's non-chart
# inferred-owner fallback badge (TPP-1543) — Team.supplyInferredOwnedBy, so
# surfaces without their own fetch of it (team profile header) still work.
# Cache-first against whatever query already populated it (e.g. the org
# chart's).
query getTeamInferredOwnerQuery($groupId: String!) {
  team: getGroup(groupId: $groupId) {
    id
    supplyInferredOwnedBy {
      ...PersonCardFragment
    }
  }
}

# Team.supplyInferredOwnedBy (TPP-1543) is always selected — the API nulls it
# itself when the relevant workspace flag is off, so the frontend doesn't
# need a separate query variant to avoid asking for it.
query getTeamAndMembersAndChildTeamsQuery(
  $groupId: String!
  $includeMembers: Boolean = true
  $membersLimit: Int = 500
  $membersOffset: Int = 0
  $membersExcludeHidden: Boolean = true
  $membersExcludeDeleted: Boolean = true
  $includeTeams: Boolean = true
  $teamsLimit: Int = 500
  $teamsOffset: Int = 0
  $includeOwners: Boolean = false
  $includePositionFields: Boolean = false
  # Separate from $includePositionFields: the count fields are @hasRole'd to
  # ADMIN/POWER/SUPPORT and the directive throws FORBIDDEN rather than
  # returning null, so a directory user must not select them at all.
  $includePositionCounts: Boolean = false
  $includeOverlayTeamDailyRate: Boolean = false
  $includeOverlayAllAttributes: Boolean = false
  $includeOverlayPersonFields: Boolean = false
) {
  team: getGroup(groupId: $groupId) {
    id
    name
    kind
    type
    dailyRate @include(if: $includeOverlayTeamDailyRate)
    isHidden
    isDeleted
    isRootForSource
    enableSupplyStructureEdits
    supplyStructureRoot {
      id
      name
      isRootForSource
      enableSupplyStructureEdits
    }
    directParentId
    hierarchyIds # needed for move group modal
    memberCount
    directMemberCount
    childTeamCount
    directChildTeamCount
    # Card badge totals. positionCount rolls descendants up like memberCount;
    # directPositionCount is this team's own seats, which is what decides
    # whether an expanded team draws a positions box.
    positionCount @include(if: $includePositionCounts)
    positionFilledCount @include(if: $includePositionCounts)
    directPositionCount @include(if: $includePositionCounts)
    supplyOwners @include(if: $includeOwners) {
      aggregateId
      firstName
      surname
      preferredName
      avatar
      jobTitle
      fte
      # Tags for reporting-line owner cards when owner ∉ team members
      tags: appliedTags {
        id
        tagId
        type
        displayValue
        isActive
        attributes
        entityTagAttributes
      }
      memberships(input: { membershipTypes: [OWNER] }) {
        currentCompositeId
        groupId
        ownerRole
      }
      supplyOwnerOf {
        id
        name
        type
        hierarchy
        hierarchyIds
        parentIds
      }
      memberOf {
        id
        name
        type
        hierarchy
        hierarchyIds
        parentIds
      }
    }
    supplyInferredOwnedBy {
      ...PersonCardFragment
    }
    attributes {
      id
      attributeType
      subtype
      label
      value
    }
    permissions {
      updateGroupAttributes
      canMutateGroup
    }
    tags: appliedTags {
      id
      tagId
      type
      displayValue
      isActive
      attributes
      tag {
        tagAttributesMetaData {
          person {
            aggregateId
            preferredName
            firstName
            surname
            avatar
          }
        }
      }
    }
    members(
      limit: $membersLimit
      offset: $membersOffset
      excludeHidden: $membersExcludeHidden
      excludeDeleted: $membersExcludeDeleted
    ) @include(if: $includeMembers) {
      customerEmployeeId
      aggregateId
      employeeId
      firstName
      surname
      preferredName
      jobTitle
      fte
      avatar
      country @include(if: $includeOverlayPersonFields)
      city @include(if: $includeOverlayPersonFields)
      state @include(if: $includeOverlayPersonFields)
      address @include(if: $includeOverlayPersonFields)
      company @include(if: $includeOverlayPersonFields)
      timezone @include(if: $includeOverlayPersonFields)
      email @include(if: $includeOverlayPersonFields)
      bands @include(if: $includeOverlayPersonFields)
      directWorkforce @include(if: $includeOverlayPersonFields)
      attributes {
        id
        attributeType
        subtype
        label
        value
      }
      allAttributes @include(if: $includeOverlayAllAttributes) {
        id
        attributeType
        subtype
        label
        value
      }
      isPlaceholder
      isVacancy
      disabled
      # Live seats this person fills — drives the org-chart position line on PersonNode.
      filledPositions @include(if: $includePositionFields) {
        id
        name
        occupancyStatus
        # Establishment FTE — the position group header shows the seat's own
        # size, not the occupants' summed FTE.
        establishedFte
        person {
          aggregateId
          preferredName
          firstName
          surname
        }
      }
      tags: appliedTags {
        id
        tagId
        type
        displayValue
        isActive
      }
      allocations {
        fte
        targetGroupId
      }
      memberOf {
        id
        name
        type
        hierarchy
        hierarchyIds
        parentIds
      }
      supplyOwnerOf {
        id
        name
        type
        hierarchy
        hierarchyIds
        parentIds
      }
      memberships(input: { membershipTypes: null }) {
        groupId
        type
        ownerRole
      }
      managing {
        aggregateId
        fte
      }
      supplyManaging {
        aggregateId
        fte
      }
      managedBy {
        aggregateId
        firstName
        preferredName
        surname
        avatar
        jobTitle
      }
      # Names required: hasPersonReportsTo → isValidPerson rejects aggregateId-only
      # stubs as UNKNOWN_NAME, which made Org Chart details show "Reports to: None".
      supplyManagedBy {
        aggregateId
        firstName
        preferredName
        surname
        avatar
        jobTitle
      }
      # TPP-1543 stand-in for supplyManagedBy when it's a gap (null) — always
      # requested, the API nulls it itself when enableDemoODInferredOwners is off.
      supplyInferredManagedBy {
        ...PersonCardFragment
      }
    }
    childTeams(limit: $teamsLimit, offset: $teamsOffset)
      @include(if: $includeTeams) {
      id
      name
      kind
      type
      dailyRate @include(if: $includeOverlayTeamDailyRate)
      isHidden
      isDeleted
      isRootForSource
      enableSupplyStructureEdits
      supplyStructureRoot {
        id
        name
        isRootForSource
        enableSupplyStructureEdits
      }
      directParentId
      hierarchyIds # needed for move group modal
      memberCount
      childTeamCount
      directMemberCount
      directChildTeamCount
      positionCount @include(if: $includePositionCounts)
      positionFilledCount @include(if: $includePositionCounts)
      directPositionCount @include(if: $includePositionCounts)
      supplyOwners @include(if: $includeOwners) {
        aggregateId
        firstName
        surname
        preferredName
        avatar
        jobTitle
        fte
        # Tags for reporting-line owner cards when owner ∉ team members
        tags: appliedTags {
          id
          tagId
          type
          displayValue
          isActive
          attributes
          entityTagAttributes
        }
        memberships(input: { membershipTypes: [OWNER] }) {
          currentCompositeId
          groupId
          ownerRole
        }
        supplyOwnerOf {
          id
          name
          type
          hierarchy
          hierarchyIds
          parentIds
        }
        memberOf {
          id
          name
          type
          hierarchy
          hierarchyIds
          parentIds
        }
      }
      supplyInferredOwnedBy {
        ...PersonCardFragment
      }
      attributes {
        id
        attributeType
        subtype
        label
        value
      }
      tags: appliedTags {
        id
        tagId
        type
        displayValue
        isActive
        attributes
        tag {
          tagAttributesMetaData {
            person {
              aggregateId
              preferredName
              firstName
              surname
              avatar
            }
          }
        }
      }
      permissions {
        updateGroupAttributes
        canMutateGroup
        canMutateMemberships
      }
    }
  }
}

query getTeamChildTeamsQuery($groupId: String!) {
  team: getGroup(groupId: $groupId) {
    id
    childTeams {
      ...TeamDetailBaseFragment
      propertyOverrides {
        directParent {
          previousDirectParentId
          groupTypeMap
        }
        groupType {
          previousType
          upstreamType
        }
      }
      tags {
        id
        type
        displayValue
        isActive
        attributes
        tagAttributesMetaData {
          person {
            aggregateId
            preferredName
            firstName
            surname
            avatar
          }
        }
      }
    }
  }
}

query getMemberCountForTeamsQuery($groupIds: [String]) {
  teams: getGroups(groupIds: $groupIds) {
    ...TeamMemberCountFragment
  }
}

query getTeamAssociationsQuery($groupId: String!) {
  team: getGroup(groupId: $groupId) {
    id
    groupAssociations {
      tag {
        id
        tagTypeMetadata {
          name
        }
        type
        displayValue
      }
      group {
        ...TeamDetailFragment
      }
      associationDirection {
        mode
        description
      }
    }
  }
}

query getGroupsQuery($groupIds: [String]) {
  getGroups(groupIds: $groupIds) {
    directParentId
    directParent {
      id
      childTeams {
        ...TeamDetailBaseFragment
        directParentId
        tags {
          id
          type
          displayValue
          isActive
          attributes
          tagAttributesMetaData {
            person {
              aggregateId
              preferredName
              firstName
              surname
              avatar
            }
          }
        }
        childTeams {
          id
          type
          directParentId
        }
        members(limit: 500) {
          ...PersonCardFragment
          ...PersonManagerFragment
          address
          country
          city
          isPlaceholder
          isVacancy
        }
      }
      members(limit: 500) {
        ...PersonCardFragment
        ...PersonManagerFragment
        address
        country
        city
        isPlaceholder
        isVacancy
      }
    }
    ...GroupFragment
  }
}

query getObjectivesQuery($objectiveIds: [String]) {
  objectives: getGroups(groupIds: $objectiveIds) {
    ...ObjectiveFragment
  }
}

query listGroupsQuery($groupTypes: [String], $includeEmptyGroups: Boolean) {
  pagedGroups: listGroups(
    groupTypes: $groupTypes
    includeEmptyGroups: $includeEmptyGroups
    includeHiddenGroups: false
  ) {
    groups {
      ...TeamFragment
      childTeams {
        ...childGroupFragment
        attributes {
          id
          attributeType
          subtype
          label
          value
        }
      }
    }
  }
}

query getParentGroups($groupId: String!, $groupTypes: [String]!) {
  groups: getGroup(groupId: $groupId) {
    id
    name
    type
    descendantGroups(groupTypes: $groupTypes) {
      id
      name
      hierarchy
      type
    }
  }
}

query getDescendantGroups($groupId: String!, $groupTypes: [String]) {
  groups: getGroup(groupId: $groupId) {
    id
    name
    type
    hierarchy
    hierarchyIds
    parentIds
    directParentId
    propertyOverrides {
      groupType {
        previousType
        upstreamType
      }
    }
    descendantGroups(groupTypes: $groupTypes) {
      id
      name
      type
      hierarchy
      hierarchyIds
      parentIds
      directParentId
      propertyOverrides {
        groupType {
          previousType
          upstreamType
        }
      }
    }
  }
}

query getGroupsByTypeQuery(
  $groupTypes: [String]
  $includeEmptyGroups: Boolean
  $includeObjectives: Boolean = false
  $includeBudgets: Boolean = false
  $valueGraphQueryOptions: ValueGraphQueryOptions = {}
) {
  pagedGroups: listGroups(
    groupTypes: $groupTypes
    includeEmptyGroups: $includeEmptyGroups
    includeHiddenGroups: false
  ) {
    groups {
      ...TeamFragment
      ...TeamBudgetsFragment @include(if: $includeBudgets)
      childTeams {
        ...childGroupFragment
        ...TeamBudgetsFragment @include(if: $includeBudgets)
        permissions {
          ...GroupPermissionsFragment
        }
      }
      permissions {
        ...GroupPermissionsFragment
      }
      objectives(valueGraphQueryOptions: $valueGraphQueryOptions)
        @include(if: $includeObjectives) {
        id
        name
        kind
        type
        attributes {
          label
          value
          attributeType
        }
        datasource {
          type
        }
      }
    }
  }
}

# Roots of the HRIS division hierarchy (kind=division groups). Used by the
# Explore menu when the enableDivisionsInExplore workspace flag is on.
query getDivisionRootGroupsQuery {
  pagedGroups: listGroups(
    groupKinds: ["division"]
    onlyRoots: true
    includeHiddenGroups: false
    includeEmptyGroups: true
  ) {
    groups {
      ...TeamFragment
      childTeams {
        ...childGroupFragment
      }
    }
  }
}

query getGroupChildTeamsQuery(
  $groupIds: [String]
  $includeBudgets: Boolean = false
) {
  groups: getGroups(groupIds: $groupIds) {
    ...baseChildGroupFragment
    ...TeamBudgetsFragment @include(if: $includeBudgets)
    childTeams {
      ...baseChildGroupFragment
      ...TeamBudgetsFragment @include(if: $includeBudgets)
      permissions {
        ...GroupPermissionsFragment
      }
    }
    permissions {
      ...GroupPermissionsFragment
    }
  }
}

# Used for useSearchGroups hook
fragment GroupBaseFragment on Group {
  id
  name
  kind
  type
  isHidden
  parentIds
  hierarchy
  hierarchyIds
}

query getGroupBaseQuery($groupId: String!) {
  group: getGroup(groupId: $groupId) {
    ...GroupBaseFragment
  }
}

query searchGroupsQuery(
  $search: [String]
  $typeFilters: [String]
  $limit: Int
  $groupId: String!
  $isLikeGroupId: Boolean!
) {
  result: listGroups(search: $search, groupTypes: $typeFilters, limit: $limit) {
    groups {
      ...GroupBaseFragment
      attributes {
        id
        attributeType
        subtype
        label
        value
      }
      tags {
        id
        type
        displayValue
        isActive
        attributes
        tagAttributesMetaData {
          person {
            aggregateId
            preferredName
            firstName
            surname
            avatar
          }
        }
      }
    }
  }
  group: getGroup(groupId: $groupId) @include(if: $isLikeGroupId) {
    ...GroupBaseFragment
    attributes {
      id
      attributeType
      subtype
      label
      value
    }
  }
}

query getTeamsByTagId($id: String!) {
  teams: getTeamsByTagId(id: $id) {
    ...GroupBaseFragment
    attributes {
      id
      attributeType
      subtype
      label
      value
    }
    tags {
      id
      type
      displayValue
      isActive
      attributes
      tagAttributesMetaData {
        person {
          aggregateId
          preferredName
          firstName
          surname
          avatar
        }
      }
    }
  }
}

query getTeamsByTagIdV2(
  $id: String!
  $limit: Int
  $offset: Int
  $filter: EntitiesByTagIdFilter
  $includeAppliedCount: Boolean = false
  $tagFilter: TagFilter
) {
  result: getTeamsByTagIdV2(
    id: $id
    limit: $limit
    offset: $offset
    filter: $filter
  ) {
    totalCount
    groups {
      id
      name
      kind
      type
      hierarchyIds
      parentIds
      isHidden
      attributes {
        id
        attributeType
        subtype
        label
        value
      }
      tags: appliedTags(filter: $tagFilter) {
        ...TagsEditorEntityTagFragment
      }
    }
  }
}

query getMemberCount(
  $groupId: String!
  $includePositionCounts: Boolean = false
) {
  group: getGroup(groupId: $groupId) {
    ...TeamMemberCountFragment
    positionCount @include(if: $includePositionCounts)
    positionFilledCount @include(if: $includePositionCounts)
    directPositionCount @include(if: $includePositionCounts)
    directPositionFilledCount @include(if: $includePositionCounts)
  }
}

query listGroupsForTeamTable(
  $limit: Int
  $offset: Int
  $includeHiddenGroups: Boolean
  $includeDeletedGroups: Boolean
  $search: [String]
  $groupTypes: [String]
) {
  result: listGroups(
    limit: $limit
    offset: $offset
    includeHiddenGroups: $includeHiddenGroups
    includeDeletedGroups: $includeDeletedGroups
    groupKinds: ["team"]
    search: $search
    groupTypes: $groupTypes
  ) {
    groups {
      id
      name
      directParentId
      hierarchyIds
      memberCount
      childTeamCount
      objectiveIds
      principles {
        met
        title
        details
      }
      attributes {
        attributeType
        subtype
        label
        value
      }
    }
    hasMore
  }
}

query getRoleBadgeMembersQuery($groupId: String!, $tagTypes: [TagType]) {
  team: getGroup(groupId: $groupId) {
    id
    members(limit: 200) {
      aggregateId
      preferredName
      firstName
      surname
      avatar
      memberTags: appliedTags(filter: { tagTypes: $tagTypes }) {
        type
        displayValue
      }
    }
  }
}
